/**
 * Contenido de la plantilla. Edita aquí textos, secciones, carta, servicios o campos de formulario: los componentes de
 * src/components/industry se adaptan solos. Las imágenes viven en src/content/website.ts (siteImages): cada sección
 * indica la posición de la imagen que usa (0 = primera). El nombre, el lema, el correo y el teléfono están en siteConfig.ts.
 */
export interface Link { label: string; href: string }

export type HeroVariant = "split" | "editorial" | "fullscreen" | "framed" | "panorama" | "minimal" | "search" | "console" | "collage" | "card" | "statement" | "centered";
export interface HeroSection {
  type: "hero"; id: string; variant: HeroVariant;
  eyebrow: string; title: string; text: string; primary: Link; secondary?: Link;
  images: number[]; stats?: { value: string; label: string }[]; note?: string;
  /** Pies de foto de las imágenes secundarias (portadas «panorama» y «collage»). */
  captions?: string[];
  /** Buscador demostrativo (portada «search»): abre el formulario de contacto con los datos elegidos. */
  search?: { fields: FormField[]; submitLabel: string; target: string };
  /** Tarjeta destacada (portada «card»): horario, cita o datos clave. */
  card?: { title: string; items: string[]; cta: Link };
  /** Muestra una maqueta de la aplicación dibujada con CSS en lugar de una foto (portada «centered»). */
  mock?: { title: string; metrics: { label: string; value: string }[]; rows: string[] };
  /** Sellos que flotan sobre la fotografía (portadas «split» y «console»): opiniones, certificaciones, disponibilidad. */
  chips?: { icon?: IconName; label: string }[];
  /** Ventana de código en lugar de la fotografía (portada «console»), útil para portafolios de desarrollo. */
  code?: { file: string; lines: string[] };
}
export type IconName =
  | "shield"
  | "clock"
  | "spark"
  | "leaf"
  | "chart"
  | "users"
  | "tool"
  | "globe"
  | "heart"
  | "check"
  | "map"
  | "cup"
  | "code"
  | "lock"
  | "bolt"
  | "home"
  | "star"
  | "plane"
  | "activity"
  | "apple"
  | "award"
  | "baby"
  | "badge-check"
  | "bed"
  | "bed-double"
  | "biceps-flexed"
  | "bike"
  | "bot"
  | "boxes"
  | "brain"
  | "briefcase"
  | "building-complex"
  | "calculator"
  | "calendar"
  | "calendar-check"
  | "calendar-clock"
  | "camera"
  | "car"
  | "cat"
  | "clipboard-check"
  | "clipboard-list"
  | "clipboard-pen"
  | "cloud"
  | "coffee"
  | "compass"
  | "concierge-bell"
  | "cpu"
  | "crosshair"
  | "dog"
  | "droplets"
  | "dumbbell"
  | "face-slightly-smiling"
  | "face-slightly-smiling-plus"
  | "file-lock"
  | "flame"
  | "flask-conical"
  | "flower-2"
  | "footprints"
  | "gauge"
  | "gavel"
  | "hand-fist"
  | "hand-heart"
  | "handshake"
  | "hard-hat"
  | "headset"
  | "heart-handshake"
  | "heart-pulse"
  | "hospital"
  | "hourglass"
  | "house"
  | "id-card"
  | "key-round"
  | "lamp"
  | "landmark"
  | "layers"
  | "layout-dashboard"
  | "luggage"
  | "medal"
  | "message-circle-question-mark"
  | "microscope"
  | "moon"
  | "mountain"
  | "music"
  | "paw-print"
  | "person-standing"
  | "pill"
  | "plug"
  | "receipt"
  | "ruler"
  | "sailboat"
  | "salad"
  | "scale"
  | "scan-eye"
  | "scroll-text"
  | "shield-check"
  | "shield-plus"
  | "ship"
  | "shopping-bag"
  | "shower-head"
  | "siren"
  | "sparkles"
  | "sprout"
  | "square-parking"
  | "stethoscope"
  | "sun"
  | "sunrise"
  | "swords"
  | "syringe"
  | "target"
  | "timer"
  | "trending-up"
  | "triangle-alert"
  | "trophy"
  | "user-check"
  | "users-round"
  | "utensils"
  | "utensils-crossed"
  | "waves"
  | "weight"
  | "wheat"
  | "wind"
  | "wine"
  | "workflow"
  | "wrench"
  | "zap";
export interface IndustriesSection { type: "industries"; id: string; eyebrow: string; title: string; items: { name: string; text: string }[] }
export interface CapabilitiesSection {
  type: "capabilities"; id: string; eyebrow: string; title: string; text: string;
  items: { code: string; title: string; text: string; specs: string[] }[];
}
export interface ProcessSection { type: "process"; id: string; eyebrow: string; title: string; image?: number; steps: { title: string; text: string }[] }
export interface GallerySection {
  type: "gallery"; id: string; variant: "facilities" | "portfolio" | "strip" | "mosaic";
  eyebrow: string; title: string; text?: string; items: { image: number; caption: string }[];
}
/**
 * Carrusel de imágenes: una foto grande a la vez, con flechas, puntos, gestos táctiles y teclado.
 * «autoplay» lo avanza solo cada «interval» milisegundos (se pausa al pasar el ratón, al enfocarlo y si el visitante
 * pidió reducir el movimiento). «text» es opcional y se muestra bajo el pie de foto.
 */
export interface CarouselSection {
  type: "carousel"; id: string; eyebrow: string; title: string; text?: string;
  autoplay?: boolean; interval?: number; items: { image: number; caption: string; text?: string }[];
}
export interface CertificationsSection { type: "certifications"; id: string; eyebrow: string; title: string; text: string; items: { name: string; text: string; icon?: IconName }[]; note: string }
export interface ServicesSection {
  type: "services"; id: string; variant: "list" | "cards"; eyebrow: string; title: string; text?: string;
  items: { name: string; text: string; duration?: string; price?: string }[]; note?: string;
}
export interface PackagesSection {
  type: "packages"; id: string; eyebrow: string; title: string; note?: string;
  items: { name: string; price: string; text: string; features: string[]; featured?: boolean }[];
}
export interface TeamSection { type: "team"; id: string; eyebrow: string; title: string; items: { name: string; role: string; text: string }[] }
/** «rating» (1 a 5) dibuja estrellas con iconos; publícalo solo si la calificación es real. */
export interface TestimonialsSection { type: "testimonials"; id: string; eyebrow: string; title: string; items: { quote: string; author: string; detail: string; rating?: number }[]; note: string }
export interface StorySection { type: "story"; id: string; eyebrow: string; title: string; paragraphs: string[]; image: number; signature?: string }
export interface MenuSection {
  type: "menu"; id: string; eyebrow: string; title: string; text?: string; note: string;
  categories: { name: string; items: { name: string; text: string; price: string; tags?: string[] }[] }[];
}
export interface SpecialtiesSection { type: "specialties"; id: string; eyebrow: string; title: string; items: { name: string; text: string; price: string; image: number }[] }
export interface HoursSection { type: "hours"; id: string; eyebrow: string; title: string; text: string }
export interface FormField {
  name: string; label: string; type: "text" | "email" | "tel" | "date" | "time" | "number" | "select" | "textarea";
  required?: boolean; options?: string[]; autocomplete?: string; half?: boolean;
}
export interface FormSection {
  type: "form"; id: string; variant: "quote" | "booking" | "reservation"; eyebrow: string; title: string; text: string;
  submitLabel: string; fields: FormField[]; aside?: string[]; note: string;
}

export interface ListingItem {
  /** «image» es la posición en siteImages; las fichas del servidor de contenido traen su foto en «imageUrl». */
  title: string; image: number; imageUrl?: string; imageAlt?: string; text: string; category?: string; location?: string; price?: string; priceNote?: string;
  meta: string[]; badge?: string; details?: { description: string; features: string[] };
  /** Enlace externo (demo, repositorio); con «href» vacío no se muestra. */
  link?: Link;
}
/** Fichas (habitaciones, destinos, propiedades, proyectos o productos) con filtro opcional y ficha detallada en un diálogo. */
export interface ListingSection {
  type: "listing"; id: string; variant: "rooms" | "tours" | "properties" | "projects" | "products";
  eyebrow: string; title: string; text?: string; filterLabel?: string; categories?: string[]; items: ListingItem[]; note?: string; cta?: Link;
}
export interface FeaturesSection { type: "features"; id: string; variant: "icons" | "list" | "cards"; eyebrow: string; title: string; text?: string; items: { icon?: IconName; title: string; text: string }[] }
export interface StatsSection { type: "stats"; id: string; eyebrow?: string; title?: string; items: { value: string; label: string }[]; note?: string }
export interface FaqSection { type: "faq"; id: string; eyebrow: string; title: string; items: { question: string; answer: string }[] }
export interface TimelineSection { type: "timeline"; id: string; eyebrow: string; title: string; items: { year: string; title: string; text: string }[] }
export interface LogosSection { type: "logos"; id: string; title: string; items: string[]; note?: string }
export interface CtaSection { type: "cta"; id: string; eyebrow?: string; title: string; text: string; primary: Link; secondary?: Link; image?: number }

export type Section = HeroSection | ListingSection | FeaturesSection | StatsSection | FaqSection | TimelineSection | LogosSection | CtaSection | IndustriesSection | CapabilitiesSection | ProcessSection | GallerySection | CarouselSection | CertificationsSection
  | ServicesSection | PackagesSection | TeamSection | TestimonialsSection | StorySection | MenuSection | SpecialtiesSection | HoursSection | FormSection;

export interface IndustryContent {
  id: string;
  /** Estructura (arquetipo) y dirección de arte: cambian la composición en styles/industry.css. */
  archetype: string;
  style: string;
  /** Estilo de diseño elegido en WebForge (minimal, neobrutalism, swiss, editorial, retro, bento…); capa de styles/looks.css. */
  look?: string;
  header: { variant: "compact" | "centered" | "transparent" | "minimal" | "stacked" | "floating"; topbar: string[]; cta: Link };
  nav: Link[];
  /** Quita, reordena o duplica secciones: la página las dibuja en este orden. */
  sections: Section[];
  contact: { address: string; hours: { days: string; time: string }[]; mapUrl: string; social: Link[] };
  footer: { about: string; legal: string };
  disclaimer: string;
  /** Botón flotante de WhatsApp (usa el teléfono de siteConfig.ts). Pon «enabled: false» para ocultarlo. */
  whatsapp?: { enabled?: boolean; message?: string; label?: string };
}

export const industry: IndustryContent = {
  "id": "veterinaria-patas-y-burbujas",
  "archetype": "product-showcase",
  "style": "groom-pop",
  "header": {
    "variant": "transparent",
    "topbar": [],
    "cta": {
      "label": "Reservar baño",
      "href": "#booking"
    }
  },
  "nav": [
    {
      "label": "Menú de estética",
      "href": "#menu"
    },
    {
      "label": "Salud",
      "href": "#health"
    },
    {
      "label": "Membresías",
      "href": "#memberships"
    },
    {
      "label": "Galería",
      "href": "#gallery"
    },
    {
      "label": "Ubicación",
      "href": "#location"
    }
  ],
  "sections": [
    {
      "type": "hero",
      "id": "hero",
      "variant": "search",
      "eyebrow": "Estética + veterinaria · Querétaro",
      "title": "Sale limpio, peinado y revisado por un veterinario",
      "text": "Baños, cortes y spa para perros y gatos con productos hipoalergénicos y una revisión médica en cada visita. Sin sedantes y sin jaulas de secado.",
      "primary": {
        "label": "Ver el menú",
        "href": "#menu"
      },
      "secondary": {
        "label": "Membresías",
        "href": "#memberships"
      },
      "images": [
        0
      ],
      "search": {
        "submitLabel": "Ver disponibilidad",
        "target": "booking",
        "fields": [
          {
            "name": "servicio",
            "label": "Servicio",
            "type": "select",
            "options": [
              "Baño completo",
              "Baño y corte",
              "Spa de hidratación",
              "Baño medicado",
              "Consulta veterinaria"
            ]
          },
          {
            "name": "tamano",
            "label": "Tamaño",
            "type": "select",
            "options": [
              "Mini (hasta 5 kg)",
              "Chico (5 a 10 kg)",
              "Mediano (10 a 25 kg)",
              "Grande (25 a 40 kg)",
              "Gigante (más de 40 kg)",
              "Gato"
            ]
          },
          {
            "name": "fecha",
            "label": "Fecha",
            "type": "date"
          }
        ]
      },
      "note": "Recoge a tu mascota en 2 a 3 horas · te avisamos por WhatsApp cuando esté lista"
    },
    {
      "type": "listing",
      "id": "menu",
      "variant": "products",
      "eyebrow": "Menú de estética",
      "title": "Elige el consentimiento de hoy",
      "text": "Precios para talla mini; el precio final depende del tamaño y del estado del pelaje.",
      "filterLabel": "Filtrar por tipo de servicio",
      "categories": [
        "Baño",
        "Corte y estilo",
        "Spa"
      ],
      "items": [
        {
          "title": "Baño completo",
          "image": 1,
          "category": "Baño",
          "text": "Shampoo según el tipo de piel, acondicionador, secado con soplador, uñas, oídos y perfume suave.",
          "price": "$280",
          "priceNote": "talla mini",
          "badge": "Más pedido",
          "meta": [
            "90 minutos",
            "Incluye revisión de piel"
          ]
        },
        {
          "title": "Baño medicado",
          "image": 8,
          "category": "Baño",
          "text": "Para dermatitis, hongos o pulgas, con el producto que indique nuestro médico veterinario.",
          "price": "$360",
          "priceNote": "talla mini",
          "meta": [
            "Con indicación médica",
            "Seguimiento incluido"
          ]
        },
        {
          "title": "Corte de raza",
          "image": 2,
          "category": "Corte y estilo",
          "text": "Estándar de la raza o el estilo que prefieras, con tijera y máquina.",
          "price": "$480",
          "priceNote": "incluye baño",
          "meta": [
            "2 a 3 horas",
            "Fotos del antes y después"
          ]
        },
        {
          "title": "Primer corte de cachorro",
          "image": 4,
          "category": "Corte y estilo",
          "text": "Una visita corta y llena de premios para que se acostumbre al ruido y al secado.",
          "price": "$380",
          "priceNote": "hasta 6 meses",
          "badge": "Para cachorros",
          "meta": [
            "Ritmo tranquilo",
            "Esquema de vacunas al día"
          ]
        },
        {
          "title": "Spa de hidratación",
          "image": 5,
          "category": "Spa",
          "text": "Mascarilla de avena, masaje relajante y cepillado profundo para pelajes largos.",
          "price": "$420",
          "priceNote": "talla mini",
          "meta": [
            "Ideal antes del invierno"
          ]
        },
        {
          "title": "Spa de patitas",
          "image": 6,
          "category": "Spa",
          "text": "Limado de uñas, corte de pelo entre almohadillas y bálsamo hidratante.",
          "price": "$220",
          "priceNote": "cualquier talla",
          "meta": [
            "30 minutos",
            "Sin cita entre semana"
          ]
        }
      ],
      "note": "Precios de ejemplo en MXN. Ajusta el menú y las tarifas reales de tu estética.",
      "cta": {
        "label": "Reservar un servicio",
        "href": "#booking"
      }
    },
    {
      "type": "features",
      "id": "health",
      "variant": "icons",
      "eyebrow": "Estética con ojo veterinario",
      "title": "Belleza que también cuida su salud",
      "items": [
        {
          "icon": "stethoscope",
          "title": "Revisión en cada baño",
          "text": "Revisamos piel, oídos, ojos y uñas; si vemos algo raro, lo consulta el médico ese mismo día."
        },
        {
          "icon": "droplets",
          "title": "Productos hipoalergénicos",
          "text": "Shampoos sin parabenos ni perfumes fuertes, elegidos según el tipo de piel."
        },
        {
          "icon": "shield-check",
          "title": "Sin sedantes",
          "text": "Trabajamos con paciencia y pausas; si tu mascota no está cómoda, paramos."
        },
        {
          "icon": "syringe",
          "title": "Vacunas al día",
          "text": "Pedimos la cartilla en la primera visita para cuidar a todos los pacientes."
        },
        {
          "icon": "wind",
          "title": "Secado sin jaulas",
          "text": "Secado a mano con soplador de baja temperatura y siempre con una persona al lado."
        },
        {
          "icon": "paw-print",
          "title": "Médico veterinario en sitio",
          "text": "Consultas, vacunas y desparasitación en la misma visita, sin trasladarte."
        }
      ]
    },
    {
      "type": "process",
      "id": "visit-steps",
      "eyebrow": "Su visita",
      "title": "Así es un día de spa",
      "image": 3,
      "steps": [
        {
          "title": "Recepción y revisión",
          "text": "Platicamos lo que quieres, revisamos su piel y anotamos cualquier sensibilidad."
        },
        {
          "title": "Baño y secado",
          "text": "Producto según su pelaje, enjuague abundante y secado a mano."
        },
        {
          "title": "Corte y detalles",
          "text": "Tijera, uñas, oídos y su moño o pañoleta si lo elegiste."
        },
        {
          "title": "Aviso y entrega",
          "text": "Te mandamos una foto por WhatsApp y te contamos cómo se portó."
        }
      ]
    },
    {
      "type": "packages",
      "id": "memberships",
      "eyebrow": "Membresías",
      "title": "Siempre limpio, sin pensarlo",
      "items": [
        {
          "name": "Burbuja",
          "price": "$250 / mes",
          "text": "Para mantener el pelaje entre cortes.",
          "features": [
            "Un baño completo al mes",
            "Corte de uñas ilimitado",
            "5 % en accesorios"
          ]
        },
        {
          "name": "Espuma",
          "price": "$480 / mes",
          "text": "El favorito de los peludos de pelo largo.",
          "features": [
            "Dos baños al mes",
            "Un corte de raza cada trimestre",
            "Revisión veterinaria trimestral",
            "Prioridad para sábados"
          ],
          "featured": true
        },
        {
          "name": "Spa total",
          "price": "$790 / mes",
          "text": "Consentimiento completo y salud al día.",
          "features": [
            "Dos baños y un spa al mes",
            "Corte mensual",
            "Vacunas y desparasitación incluidas",
            "Recogida a domicilio en la zona"
          ]
        }
      ],
      "note": "Precios de ejemplo en MXN para talla mini o chica; las membresías se ajustan al tamaño de tu mascota."
    },
    {
      "type": "gallery",
      "id": "gallery",
      "variant": "strip",
      "eyebrow": "Galería",
      "title": "Recién salidos del spa",
      "text": "Desliza para ver nuestras cabinas y algunos clientes consentidos.",
      "items": [
        {
          "image": 7,
          "caption": "Cabinas de estética"
        },
        {
          "image": 0,
          "caption": "Secado a mano"
        },
        {
          "image": 2,
          "caption": "Corte de raza"
        },
        {
          "image": 5,
          "caption": "Después del spa"
        },
        {
          "image": 8,
          "caption": "Baño para gigantes"
        }
      ]
    },
    {
      "type": "testimonials",
      "id": "reviews",
      "eyebrow": "Clientes felices",
      "title": "Huelen bien y vuelven contentos",
      "items": [
        {
          "quote": "Chispa llegaba temblando a otras estéticas. Aquí la recibieron con premios y me mandaron foto a media sesión.",
          "author": "Dueña de Chispa",
          "detail": "Opinión de ejemplo",
          "rating": 5
        },
        {
          "quote": "En el baño detectaron una otitis y la doctora la revisó ahí mismo. Ahorramos una vuelta.",
          "author": "Dueño de Max",
          "detail": "Opinión de ejemplo",
          "rating": 5
        },
        {
          "quote": "La membresía Espuma vale cada peso: Rocco siempre está impecable.",
          "author": "Dueña de Rocco",
          "detail": "Opinión de ejemplo",
          "rating": 4
        }
      ],
      "note": "Opiniones de ejemplo: sustitúyelas por reseñas reales publicadas con autorización de sus autores."
    },
    {
      "type": "faq",
      "id": "faq",
      "eyebrow": "Preguntas frecuentes",
      "title": "Antes de reservar",
      "items": [
        {
          "question": "¿Cuánto tarda el servicio?",
          "answer": "Entre 90 minutos y 3 horas según el tamaño y el servicio. Te avisamos por WhatsApp cuando esté listo."
        },
        {
          "question": "¿Atienden gatos?",
          "answer": "Sí, en horario exclusivo por la mañana y con una estilista especializada en felinos."
        },
        {
          "question": "¿Qué pasa si tiene pulgas o la piel irritada?",
          "answer": "Nuestro médico lo revisa y aplicamos un baño medicado; te explicamos el tratamiento para casa."
        },
        {
          "question": "¿Puedo quedarme durante el baño?",
          "answer": "En la primera visita sí, para que tu mascota se sienta segura. Después recomendamos esperar fuera: se relajan más."
        }
      ]
    },
    {
      "type": "hours",
      "id": "location",
      "eyebrow": "Ubicación",
      "title": "Ven a conocernos",
      "text": "Estacionamiento frente a la estética y área de espera con agua fresca para tu mascota."
    },
    {
      "type": "form",
      "id": "booking",
      "variant": "booking",
      "eyebrow": "Reservaciones",
      "title": "Aparta su lugar en el spa",
      "text": "Elige servicio, tamaño y fecha; te confirmamos el horario por WhatsApp.",
      "submitLabel": "Reservar",
      "aside": [
        "Confirmación en menos de una hora",
        "Recordatorio el día anterior",
        "Cambios sin costo con 24 horas de aviso"
      ],
      "fields": [
        {
          "name": "nombre",
          "label": "Tu nombre",
          "type": "text",
          "required": true,
          "autocomplete": "name",
          "half": true
        },
        {
          "name": "telefono",
          "label": "WhatsApp",
          "type": "tel",
          "required": true,
          "autocomplete": "tel",
          "half": true
        },
        {
          "name": "mascota",
          "label": "Nombre y raza de tu mascota",
          "type": "text",
          "required": true
        },
        {
          "name": "servicio",
          "label": "Servicio",
          "type": "select",
          "required": true,
          "options": [
            "Baño completo",
            "Baño y corte",
            "Spa de hidratación",
            "Baño medicado",
            "Consulta veterinaria"
          ],
          "half": true
        },
        {
          "name": "tamano",
          "label": "Tamaño",
          "type": "select",
          "required": true,
          "options": [
            "Mini (hasta 5 kg)",
            "Chico (5 a 10 kg)",
            "Mediano (10 a 25 kg)",
            "Grande (25 a 40 kg)",
            "Gigante (más de 40 kg)",
            "Gato"
          ],
          "half": true
        },
        {
          "name": "fecha",
          "label": "Fecha",
          "type": "date",
          "required": true,
          "half": true
        },
        {
          "name": "hora",
          "label": "Hora preferida",
          "type": "time",
          "half": true
        },
        {
          "name": "comentarios",
          "label": "Sensibilidades o indicaciones",
          "type": "textarea"
        }
      ],
      "note": "El formulario abre el correo del visitante con los datos de la reserva; no guarda información en ningún servidor."
    }
  ],
  "contact": {
    "address": "Av. Universidad 310, Col. Centro, Santiago de Querétaro, Qro.",
    "hours": [
      {
        "days": "Martes a viernes",
        "time": "9:00 – 19:00"
      },
      {
        "days": "Sábado",
        "time": "8:00 – 16:00"
      },
      {
        "days": "Domingo y lunes",
        "time": "Cerrado"
      }
    ],
    "mapUrl": "https://www.openstreetmap.org/search?query=Santiago%20de%20Quer%C3%A9taro",
    "social": [
      {
        "label": "Instagram",
        "href": "https://www.instagram.com/"
      },
      {
        "label": "TikTok",
        "href": "https://www.tiktok.com/"
      },
      {
        "label": "Facebook",
        "href": "https://www.facebook.com/"
      }
    ]
  },
  "footer": {
    "about": "Baños, cortes, spa y consulta veterinaria para perros y gatos en un mismo lugar.",
    "legal": "Contenido de ejemplo generado con WebForge Studio. Los servicios veterinarios requieren valoración profesional."
  },
  "whatsapp": {
    "message": "Hola, Patas & Burbujas. Quiero reservar un baño para mi mascota.",
    "label": "Reserva por WhatsApp"
  },
  "disclaimer": "Negocio ficticio. Nombres, precios, horarios y opiniones son de ejemplo; no se publican cédulas ni certificaciones.",
  "look": "original"
};
